import React, { useState, useCallback, useEffect } from 'react';
import { billingAdminService } from '../services/api';
import {
  Gift, RefreshCw, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Download, X,
  CheckCircle, Clock, IndianRupee,
  Copy, Ban, AlertTriangle,
} from 'lucide-react';
import './BillingManagement.css';
import './BillingPages.css';

const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtINR = (v) => `₹${fmt(v)}`;
const copyId = (text) => { navigator.clipboard.writeText(text).catch(() => {}); };

const relativeDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const fullDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const reasonColors = {
  promo: { bg: '#e8f5e9', color: '#2e7d32', label: 'Promotional' },
  refund: { bg: '#fff3e0', color: '#e65100', label: 'Refund' },
  compensation: { bg: '#fce4ec', color: '#c62828', label: 'Compensation' },
  partner: { bg: '#e3f2fd', color: '#1565c0', label: 'Partner' },
  manual: { bg: '#f3e5f5', color: '#7b1fa2', label: 'Manual' },
};

const ReasonBadge = ({ reason }) => {
  const cfg = reasonColors[reason] || { bg: '#f5f5f5', color: '#666', label: reason };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem', fontWeight: 500, background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
};

// ── Grant Credit Modal ────────────────────────────────────────
const GrantCreditModal = ({ onClose, onSuccess, accounts }) => {
  const [form, setForm] = useState({ billing_account_id: '', amount: '', reason: 'promo', description: '', expires_in_days: '365' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountList, setAccountList] = useState([]);
  const [accountLoading, setAccountLoading] = useState(false);

  // Fetch accounts for picker
  useEffect(() => {
    if (!accountSearch || accountSearch.length < 2) { setAccountList([]); return; }
    const timer = setTimeout(async () => {
      try {
        setAccountLoading(true);
        const data = await billingAdminService.getAccounts({ search: accountSearch, pageSize: 10 });
        setAccountList(data.accounts || []);
      } catch { setAccountList([]); }
      finally { setAccountLoading(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [accountSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.billing_account_id) { setError('Select a billing account'); return; }
    setLoading(true); setError(null);
    try {
      await billingAdminService.grantCredit({
        billing_account_id: form.billing_account_id,
        amount: parseFloat(form.amount),
        reason: form.reason,
        description: form.description,
        expires_in_days: parseInt(form.expires_in_days),
      });
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to grant credit');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2><Gift size={18} /> Grant Ad Credit</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

            <div className="form-group">
              <label>Billing Account *</label>
              {form.billing_account_id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: '#f0f4ff', borderRadius: 8 }}>
                  <span style={{ fontWeight: 500 }}>{accountList.find(a => a.id === form.billing_account_id)?.tenant_name || form.billing_account_id.slice(0, 12)}</span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm(f => ({ ...f, billing_account_id: '' }))}>
                    <X size={12} /> Change
                  </button>
                </div>
              ) : (
                <>
                  <input type="text" placeholder="Search tenant name…" value={accountSearch}
                    onChange={e => setAccountSearch(e.target.value)} />
                  {accountLoading && <span style={{ fontSize: '0.8rem', color: '#999' }}>Searching…</span>}
                  {accountList.length > 0 && (
                    <div style={{ border: '1px solid #eee', borderRadius: 8, maxHeight: 160, overflow: 'auto', marginTop: 4 }}>
                      {accountList.map(a => (
                        <div key={a.id} style={{ padding: '0.5rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', fontSize: '0.875rem' }}
                          onClick={() => { setForm(f => ({ ...f, billing_account_id: a.id })); setAccountList(prev => prev); }}>
                          <strong>{a.tenant_name}</strong> <span style={{ color: '#999', fontSize: '0.8rem' }}>({a.id.slice(0, 8)}…)</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="form-group">
              <label>Amount (₹) *</label>
              <input type="number" min="1" step="0.01" required placeholder="e.g. 500"
                value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Reason *</label>
              <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}>
                <option value="promo">Promotional</option>
                <option value="refund">Refund</option>
                <option value="compensation">Compensation</option>
                <option value="partner">Partner Program</option>
                <option value="manual">Manual Adjustment</option>
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <input type="text" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="E.g. onboarding bonus, service disruption…" />
            </div>
            <div className="form-group">
              <label>Expires in (days)</label>
              <input type="number" min="1" max="730"
                value={form.expires_in_days} onChange={e => setForm(f => ({ ...f, expires_in_days: e.target.value }))} />
              <span className="form-hint">Default: 365 days.</span>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><div className="btn-spinner" /> Granting…</> : <><Gift size={14} /> Grant Credit</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Credit Detail Drawer ──────────────────────────────────────
const CreditDrawer = ({ credit, onClose, onRefresh }) => {
  const [showRevoke, setShowRevoke] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokeMessage, setRevokeMessage] = useState(null);
  if (!credit) return null;

  const consumedPct = parseFloat(credit.original_amount) > 0
    ? ((parseFloat(credit.original_amount) - parseFloat(credit.remaining_amount)) / parseFloat(credit.original_amount)) * 100
    : 0;

  const isExpired = credit.expires_at && new Date(credit.expires_at) <= new Date();
  const isConsumed = parseFloat(credit.remaining_amount) === 0;

  const daysUntilExpiry = credit.expires_at
    ? Math.ceil((new Date(credit.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <>
      <div className="detail-drawer-backdrop" onClick={onClose} />
      <div className="detail-drawer">
        <div className="detail-drawer-header">
          <h2><Gift size={18} /> Credit Detail</h2>
          <button className="btn btn-secondary btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="detail-drawer-body">
          {/* Header */}
          <div className="detail-drawer-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>{fmtINR(credit.original_amount)}</span>
              <ReasonBadge reason={credit.reason} />
              {isExpired && <span className="status-badge status-disabled">Expired</span>}
              {isConsumed && !isExpired && <span className="status-badge status-muted">Consumed</span>}
              {!isExpired && !isConsumed && <span className="status-badge status-active">Active</span>}
            </div>
          </div>

          {/* Consumption */}
          <div className="detail-drawer-section">
            <h3>Consumption</h3>
            <div style={{ marginBottom: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                <span>Used: {fmtINR(parseFloat(credit.original_amount) - parseFloat(credit.remaining_amount))}</span>
                <span>Remaining: {fmtINR(credit.remaining_amount)}</span>
              </div>
              <div className="credit-consumption-bar">
                <div className="credit-consumption-fill" style={{
                  width: `${consumedPct}%`,
                  background: consumedPct >= 100 ? '#999' : consumedPct >= 75 ? '#f39c12' : '#27ae60',
                }} />
              </div>
              <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>{Math.round(consumedPct)}% consumed</div>
            </div>
          </div>

          {/* Details */}
          <div className="detail-drawer-section">
            <h3>Details</h3>
            <div className="drawer-detail-grid">
              <div className="drawer-detail-item">
                <label>Tenant</label>
                <span>{credit.tenant_name || '—'}</span>
              </div>
              <div className="drawer-detail-item">
                <label>Reason</label>
                <span><ReasonBadge reason={credit.reason} /></span>
              </div>
              <div className="drawer-detail-item full-width">
                <label>Description</label>
                <span>{credit.description || '—'}</span>
              </div>
              <div className="drawer-detail-item">
                <label>Granted By</label>
                <span>{credit.granted_by_email || '—'}</span>
              </div>
              <div className="drawer-detail-item">
                <label>Granted At</label>
                <span>{fullDate(credit.created_at)}</span>
              </div>
              <div className="drawer-detail-item">
                <label>Expires At</label>
                <span>
                  {credit.expires_at ? fullDate(credit.expires_at) : 'Never'}
                  {daysUntilExpiry !== null && daysUntilExpiry > 0 && (
                    <span style={{ color: daysUntilExpiry <= 30 ? '#f39c12' : '#999', fontSize: '0.8rem', marginLeft: 4 }}>
                      ({daysUntilExpiry}d left)
                    </span>
                  )}
                  {daysUntilExpiry !== null && daysUntilExpiry <= 0 && (
                    <span style={{ color: '#dc3545', fontSize: '0.8rem', marginLeft: 4 }}>(expired)</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* IDs */}
          <div className="detail-drawer-section">
            <h3>Reference IDs</h3>
            <div className="drawer-detail-grid">
              <div className="drawer-detail-item full-width">
                <label>Credit ID</label>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {credit.id}
                  <button className="copy-btn" onClick={() => copyId(credit.id)} title="Copy" style={{ marginLeft: 4 }}><Copy size={10} /></button>
                </span>
              </div>
              <div className="drawer-detail-item full-width">
                <label>Billing Account ID</label>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {credit.billing_account_id}
                  <button className="copy-btn" onClick={() => copyId(credit.billing_account_id)} title="Copy" style={{ marginLeft: 4 }}><Copy size={10} /></button>
                </span>
              </div>
            </div>
          </div>

          {/* Revoke Action */}
          {!isConsumed && !isExpired && (
            <div className="detail-drawer-section">
              <h3>Actions</h3>
              {revokeMessage && (
                <div className={`alert ${revokeMessage.type === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                  {revokeMessage.text}
                </div>
              )}
              {!showRevoke ? (
                <button className="btn btn-sm" onClick={() => setShowRevoke(true)}
                  style={{ background: '#fff', border: '1px solid #dc3545', color: '#dc3545' }}>
                  <Ban size={13} /> Revoke Credit
                </button>
              ) : (
                <div className="action-panel">
                  <div className="action-panel-header" style={{ color: '#dc3545' }}>
                    <AlertTriangle size={14} /> <strong>Revoke {fmtINR(credit.remaining_amount)} Remaining Credit</strong>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 0' }}>
                    This will zero out the remaining balance and deduct {fmtINR(credit.remaining_amount)} from
                    <strong> {credit.tenant_name}</strong>'s credit balance. This action cannot be undone.
                  </p>
                  <div className="form-group" style={{ marginTop: '0.5rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Reason * (audit trail, min 5 chars)</label>
                    <input type="text" value={revokeReason} onChange={e => setRevokeReason(e.target.value)}
                      placeholder="e.g. Issued in error, duplicate credit, account abuse…" style={{ fontSize: '0.85rem' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setShowRevoke(false); setRevokeMessage(null); }}>Cancel</button>
                    <button className="btn btn-sm" disabled={revokeLoading || revokeReason.trim().length < 5}
                      style={{ background: '#dc3545', color: '#fff', border: 'none' }}
                      onClick={async () => {
                        setRevokeLoading(true); setRevokeMessage(null);
                        try {
                          await billingAdminService.revokeCredit(credit.id, { reason: revokeReason.trim() });
                          setRevokeMessage({ type: 'success', text: `Revoked ${fmtINR(credit.remaining_amount)}` });
                          setShowRevoke(false);
                          onRefresh?.();
                        } catch (err) {
                          setRevokeMessage({ type: 'error', text: err.response?.data?.error || 'Revoke failed' });
                        } finally { setRevokeLoading(false); }
                      }}>
                      {revokeLoading ? <><div className="btn-spinner" /> Revoking…</> : <><Ban size={13} /> Confirm Revoke</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ── CSV Export ─────────────────────────────────────────────────
const exportCSV = (credits) => {
  const headers = ['Tenant', 'Amount', 'Remaining', 'Reason', 'Description', 'Granted By', 'Granted At', 'Expires At'];
  const rows = credits.map(c => [
    c.tenant_name, c.original_amount, c.remaining_amount, c.reason,
    c.description || '', c.granted_by_email || '', c.created_at || '', c.expires_at || '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `credits-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ── Main Page ─────────────────────────────────────────────────
const BillingCredits = () => {
  const [credits, setCredits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sorting, setSorting] = useState({ sortBy: 'created_at', sortOrder: 'desc' });
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);

  const fetchCredits = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getCredits({
        page: pagination.page, pageSize: pagination.pageSize,
        sortBy: sorting.sortBy, sortOrder: sorting.sortOrder,
        ...(search && { search }), ...(reasonFilter && { reason: reasonFilter }),
        ...(statusFilter && { status: statusFilter }),
      });
      setCredits(data.credits || []);
      setSummary(data.summary || null);
      if (data.pagination) setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (err) { console.error('Failed to load credits:', err); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.pageSize, search, reasonFilter, statusFilter, sorting]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  const handleSort = (field) => {
    setSorting(prev => ({ sortBy: field, sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc' }));
  };
  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return <ArrowUpDown size={12} className="sort-icon" />;
    return sorting.sortOrder === 'asc' ? <ArrowUp size={12} className="sort-icon active" /> : <ArrowDown size={12} className="sort-icon active" />;
  };

  const showStart = Math.min(((pagination.page - 1) * pagination.pageSize) + 1, pagination.totalCount);
  const showEnd = Math.min(pagination.page * pagination.pageSize, pagination.totalCount);

  const getCreditStatus = (c) => {
    if (parseFloat(c.remaining_amount) === 0) return { label: 'Consumed', class: 'status-muted' };
    if (c.expires_at && new Date(c.expires_at) <= new Date()) return { label: 'Expired', class: 'status-disabled' };
    const daysLeft = c.expires_at ? Math.ceil((new Date(c.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null;
    if (daysLeft !== null && daysLeft <= 30) return { label: `${daysLeft}d left`, class: 'status-pending' };
    return { label: 'Active', class: 'status-active' };
  };

  return (
    <div className="billing-management">
      <div className="billing-page-header">
        <h1><Gift size={22} /> Ad Credits</h1>
        <div className="header-actions">
          <button className="btn btn-secondary btn-sm" onClick={fetchCredits} title="Refresh"><RefreshCw size={14} /></button>
          {credits.length > 0 && (
            <button className="btn-export" onClick={() => exportCSV(credits)}><Download size={14} /> Export CSV</button>
          )}
          <button className="btn btn-primary" onClick={() => setShowGrantModal(true)}>
            <Gift size={14} /> Grant Credit
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="billing-summary-cards">
          <div className="billing-summary-card">
            <h4><IndianRupee size={13} /> Total Granted</h4>
            <div className="summary-value">{fmtINR(summary.total_granted)}</div>
            <div className="summary-sub">all time</div>
          </div>
          <div className="billing-summary-card card-success">
            <h4><CheckCircle size={13} /> Currently Active</h4>
            <div className="summary-value">{fmtINR(summary.total_remaining)}</div>
            <div className="summary-sub">remaining balance</div>
          </div>
          <div className="billing-summary-card card-info">
            <h4><Gift size={13} /> Consumed</h4>
            <div className="summary-value">{fmtINR(summary.total_consumed)}</div>
            <div className="summary-sub">applied to charges</div>
          </div>
          <div className="billing-summary-card card-warning">
            <h4><Clock size={13} /> Expiring (30d)</h4>
            <div className="summary-value">{fmtINR(summary.expiring_30d)}</div>
            <div className="summary-sub">{parseFloat(summary.expiring_30d) > 0 ? 'action needed' : 'none upcoming'}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="billing-filters">
        <input type="text" className="search-input" placeholder="Search by tenant name…"
          value={search} onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
        <select value={reasonFilter} onChange={e => { setReasonFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Reasons</option>
          <option value="promo">Promotional</option>
          <option value="refund">Refund</option>
          <option value="compensation">Compensation</option>
          <option value="partner">Partner</option>
          <option value="manual">Manual</option>
        </select>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="consumed">Consumed</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : credits.length === 0 ? (
        <div className="billing-card">
          <div className="billing-empty">
            <Gift size={40} />
            <p>No credits found</p>
            <span>Use "Grant Credit" to award promotional or refund credits to tenants</span>
          </div>
        </div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th className="sortable" onClick={() => handleSort('original_amount')}>Amount {getSortIcon('original_amount')}</th>
                  <th className="sortable" onClick={() => handleSort('remaining_amount')}>Remaining {getSortIcon('remaining_amount')}</th>
                  <th>Reason</th>
                  <th>Granted By</th>
                  <th className="sortable" onClick={() => handleSort('created_at')}>Granted {getSortIcon('created_at')}</th>
                  <th className="sortable" onClick={() => handleSort('expires_at')}>Expires {getSortIcon('expires_at')}</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {credits.map(c => {
                  const status = getCreditStatus(c);
                  const consumedPct = parseFloat(c.original_amount) > 0
                    ? ((parseFloat(c.original_amount) - parseFloat(c.remaining_amount)) / parseFloat(c.original_amount)) * 100 : 0;
                  return (
                    <tr key={c.id} className="clickable-row" onClick={() => setSelectedCredit(c)}>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">{(c.tenant_name || '?').charAt(0)}</div>
                          <span className="user-name">{c.tenant_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td><span className="amount-cell">{fmtINR(c.original_amount)}</span></td>
                      <td>
                        <span className="amount-cell" style={{ color: parseFloat(c.remaining_amount) > 0 ? '#27ae60' : '#999' }}>
                          {fmtINR(c.remaining_amount)}
                        </span>
                        <div className="credit-consumption-bar" style={{ width: 60, display: 'inline-block', marginLeft: 8, verticalAlign: 'middle' }}>
                          <div className="credit-consumption-fill" style={{ width: `${consumedPct}%` }} />
                        </div>
                      </td>
                      <td><ReasonBadge reason={c.reason} /></td>
                      <td className="text-muted" style={{ fontSize: '0.85rem' }}>{c.granted_by_email || '—'}</td>
                      <td className="text-muted">{relativeDate(c.created_at)}</td>
                      <td className="text-muted">{c.expires_at ? relativeDate(c.expires_at) : 'Never'}</td>
                      <td><span className={`status-badge ${status.class}`}>{status.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 0 && (
            <div className="pagination">
              <span>Showing {showStart}–{showEnd} of {pagination.totalCount} credits</span>
              <div className="pagination-buttons">
                <button className="btn btn-secondary btn-sm" disabled={pagination.page <= 1}
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
                  <ChevronLeft size={14} /> Prev
                </button>
                <button className="btn btn-secondary btn-sm" disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail drawer */}
      {selectedCredit && <CreditDrawer credit={selectedCredit} onClose={() => setSelectedCredit(null)} onRefresh={() => { setSelectedCredit(null); fetchCredits(); }} />}

      {/* Grant modal */}
      {showGrantModal && (
        <GrantCreditModal
          onClose={() => setShowGrantModal(false)}
          onSuccess={() => { setShowGrantModal(false); fetchCredits(); }}
        />
      )}
    </div>
  );
};

export default BillingCredits;
